<script setup lang="ts">
// The Product-Insights KPI cards — port of v3 `ProductInsightsCards.vue` (ORP-18953,
// contract 27 §10). The product-derived report reads live in `useProductInsightCounts`; the v3
// `console.error` swallows became the loading/error/ready states, and the card click's
// `window.location.hash` + `history.replaceState` hack became a plain `scrollIntoView`
// (contract §12). The Catalog-Issue-Types card is NOT interactive here: its only v3 effect
// was forcing the issues section into chart view, a seam v4 sections do not expose (PR-flagged).
import { useProductInsightCounts } from '../../composables/useProductInsightCounts'
import type { ProductInsightCounts } from '../../models/productInsights.types'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button, Skeleton } from '@/shared/ui'

interface CardDefinition {
  key: keyof ProductInsightCounts
  labelKey: string
  /** v3 `hashMap` targets (`ProductInsights.vue:164–168`); null = no anchor in v3 either. */
  target: string | null
}

const CARDS: CardDefinition[] = [
  {
    key: 'productIssues',
    labelKey: 'commerceAnalytics.productInsights.cards.productIssues',
    target: 'productIssues',
  },
  {
    key: 'productIssueTypes',
    labelKey: 'commerceAnalytics.productInsights.cards.productIssueTypes',
    target: null,
  },
  {
    key: 'demandIncreasedProducts',
    labelKey: 'commerceAnalytics.productInsights.cards.demandIncreasedProducts',
    target: 'demandIncreasedProducts',
  },
]

const { t } = useI18n()
const format = useFormat()
const { counts, state, reload } = useProductInsightCounts()

/** v3 rendered every count through `formatFixed` — `heavyDefault` is its funnel twin. */
function cardValue(key: keyof ProductInsightCounts): string {
  return format.formatNumber(counts.value[key], { heavyDefault: true })
}

function scrollToSection(target: string): void {
  document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <div
    class="product-insight-cards grid grid-cols-2 gap-sm lg:grid-cols-4"
    :aria-busy="state === 'loading' || undefined"
  >
    <template v-if="state === 'loading'">
      <Skeleton v-for="index in CARDS.length" :key="index" height="5.5rem" />
    </template>

    <div
      v-else-if="state === 'error'"
      class="product-insight-cards__error flex flex-wrap items-center justify-center gap-sm col-span-full p-md text-muted"
      role="alert"
    >
      <p>{{ t('commerceAnalytics.productInsights.cards.error') }}</p>
      <Button variant="outline" size="sm" @click="reload">
        {{ t('commerceAnalytics.productInsights.cards.retry') }}
      </Button>
    </div>

    <template v-else>
      <component
        :is="card.target ? 'button' : 'div'"
        v-for="card in CARDS"
        :key="card.key"
        :type="card.target ? 'button' : undefined"
        class="product-insight-cards__card flex flex-col items-center gap-2xs p-md border border-border rounded-lg bg-surface shadow-card [font:inherit] text-center"
        :class="{ 'product-insight-cards__card--link': card.target }"
        @click="card.target && scrollToSection(card.target)"
      >
        <span class="product-insight-cards__label text-primary text-[0.875rem] font-semibold">{{
          t(card.labelKey)
        }}</span>
        <span class="product-insight-cards__value text-accent text-[1.875rem] font-bold">{{
          cardValue(card.key)
        }}</span>
      </component>
    </template>
  </div>
</template>

<style scoped>
.product-insight-cards .product-insight-cards__card--link {
  cursor: pointer;
}
.product-insight-cards .product-insight-cards__card--link:hover {
  background-color: var(--ui-color-surface-sunken);
}
</style>
